import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Repeat } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { catLabel, FREQUENCIES } from "./categories";
import { ExpenseModal } from "./ExpensesTab";

const VIOLET = "#6D5DF6";

function DueBadge({ e }) {
  if (e.days_until_due === null || e.days_until_due === undefined) return <span className="text-neutral-300">—</span>;
  const dateLabel = new Date(e.next_due + "T12:00:00").toLocaleDateString("fr-FR");
  if (e.days_until_due < 0)
    return <span className="bg-rose-50 text-rose-500 rounded-full px-2 py-0.5 text-[10px] font-medium" data-testid={`charge-overdue-${e.id}`}>En retard · {dateLabel}</span>;
  if (e.days_until_due <= 7)
    return <span className="bg-amber-50 text-amber-600 rounded-full px-2 py-0.5 text-[10px] font-medium" data-testid={`charge-due-soon-${e.id}`}>J-{e.days_until_due} · {dateLabel}</span>;
  return <span className="text-neutral-500 text-[11px]">{dateLabel}</span>;
}

export default function AnnualChargesTab() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // null | "new" | expense
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/pilotage/annual-charges").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="py-10 text-center text-xs text-neutral-400">Chargement…</p>;
  const shown = data.items.filter((i) => filter === "all" || i.type === filter);
  const del = async (e) => {
    if (!window.confirm(`Supprimer « ${e.label} » ?`)) return;
    try { await api.delete(`/pilotage/expenses/${e.id}`); toast.success("Charge supprimée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const cards = [
    ["Charges pro annualisées", data.total_pro, VIOLET],
    ["Charges perso annualisées", data.total_perso, "#EA580C"],
    ["Coût mensuel équivalent", data.monthly_equivalent, "#0D9488"],
  ];
  return (
    <div className="space-y-4" data-testid="annual-charges-tab">
      <div className="grid grid-cols-3 gap-3">
        {cards.map(([l, v, c]) => (
          <div key={l} className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`charges-card-${l.slice(0, 12).replace(/\W/g, "-")}`}>
            <p className="text-[11px] text-neutral-400 flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5" style={{ color: c }} />{l}</p>
            <p className="text-xl font-bold text-[#0B132B] mt-1">{eur(v)}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-neutral-100 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-neutral-100">
          <p className="text-sm font-semibold text-[#0B132B]">Charges récurrentes</p>
          <div className="flex rounded-lg border border-neutral-200 overflow-hidden ml-2">
            {[["all", "Toutes"], ["pro", "Pro"], ["perso", "Perso"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`charges-filter-${v}`}
                className={`px-3 py-1.5 text-xs transition-colors ${filter === v ? "text-white" : "bg-white text-neutral-500 hover:bg-neutral-50"}`}
                style={filter === v ? { background: VIOLET } : {}}>{l}</button>
            ))}
          </div>
          <div className="flex-1" />
          <button onClick={() => setModal("new")} className="flex items-center gap-1.5 text-white text-xs font-medium rounded-lg px-3 py-1.5" style={{ background: VIOLET }} data-testid="charges-add">
            <Plus className="w-3.5 h-3.5" /> Ajouter une charge
          </button>
        </div>
        {shown.length === 0 ? (
          <p className="py-10 text-center text-xs text-neutral-400" data-testid="charges-empty">Aucune charge récurrente — créez une dépense avec une fréquence mensuelle, trimestrielle ou annuelle.</p>
        ) : (
          <table className="w-full text-xs" data-testid="charges-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-neutral-400 border-b border-neutral-100">
                <th className="px-4 py-2 font-medium">Intitulé</th><th className="px-2 py-2 font-medium">Type</th>
                <th className="px-2 py-2 font-medium">Catégorie</th><th className="px-2 py-2 font-medium">Fréquence</th>
                <th className="px-2 py-2 font-medium text-right">Montant</th><th className="px-2 py-2 font-medium text-right">Coût annuel</th>
                <th className="px-2 py-2 font-medium text-right">Équiv. mensuel</th><th className="px-2 py-2 font-medium">Prochaine échéance</th>
                <th className="px-2 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr key={e.id} className="border-b border-neutral-50 hover:bg-neutral-50/50" data-testid={`charge-row-${e.id}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-neutral-800">{e.label}</p>
                    {e.vendor && <p className="text-[10px] text-neutral-400">{e.vendor}</p>}
                  </td>
                  <td className="px-2 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${e.type === "pro" ? "bg-violet-50 text-violet-600" : "bg-orange-50 text-orange-500"}`}>
                      {e.type === "pro" ? "Pro" : "Perso"}
                    </span>
                  </td>
                  <td className="px-2 py-2.5"><span className="bg-neutral-100 text-neutral-500 rounded-full px-2 py-0.5 text-[10px]">{catLabel(e.type, e.category)}</span></td>
                  <td className="px-2 py-2.5 text-neutral-500">{FREQUENCIES[e.frequency]}</td>
                  <td className="px-2 py-2.5 text-right font-medium text-neutral-800">{eur(e.effective_amount)}</td>
                  <td className="px-2 py-2.5 text-right font-semibold" style={{ color: VIOLET }}>{eur(e.annual_total)}</td>
                  <td className="px-2 py-2.5 text-right text-neutral-500">{eur(e.monthly_equivalent)}</td>
                  <td className="px-2 py-2.5"><DueBadge e={e} /></td>
                  <td className="px-2 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setModal(e)} className="p-1 text-neutral-400 hover:text-neutral-700" data-testid={`charge-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(e)} className="p-1 text-neutral-400 hover:text-rose-500" data-testid={`charge-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {modal && <ExpenseModal type={modal === "new" ? "pro" : modal.type} expense={modal === "new" ? null : modal}
        onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} switchType={modal === "new" ? () => {} : undefined} />}
    </div>
  );
}
